import { useEffect, useRef, useState } from "react";
import { cn } from "@fox1ck-org/ui";
import { Play } from "lucide-react";
import { LanderType } from "@gen/tracker/v1/lander_pb";

/**
 * The page's sketch (boards LanderList, LanderTiles, LanderEmpty): what a
 * page of this type looks like, drawn in the package's tokens — the shape a
 * tile shows before (or instead of) the page itself. Not in the package: a
 * picture of a lander is this app's subject, not a primitive.
 */
export function LanderSketch({ type, className }: { type: LanderType; className?: string }) {
  return (
    <div aria-hidden className={cn("relative h-full w-full overflow-hidden bg-surface-2", className)}>
      <div className="absolute inset-[8%] flex flex-col gap-[5%]">{SKETCH[type] ?? SKETCH[LanderType.LANDING]}</div>
    </div>
  );
}

const Line = ({ w, strong, tone }: { w: string; strong?: boolean; tone?: "primary" | "danger" }) => (
  <span
    className={cn(
      "block shrink-0 rounded-full",
      strong ? "h-[7%] min-h-1.5" : "h-[3.5%] min-h-1",
      tone === "primary" ? "bg-primary" : tone === "danger" ? "bg-destructive" : strong ? "bg-ink-3" : "bg-border-strong",
    )}
    style={{ width: w }}
  />
);
const Block = ({ h, className, children }: { h: string; className?: string; children?: React.ReactNode }) => (
  <span className={cn("block shrink-0 rounded-sm bg-surface-3", className)} style={{ height: h }}>
    {children}
  </span>
);

const SKETCH: Partial<Record<LanderType, React.ReactNode>> = {
  [LanderType.LANDING]: (
    <>
      <span className="flex h-[48%] shrink-0 gap-[6%]">
        <Block h="100%" className="flex flex-[1.1] items-center justify-center">
          <span className="block h-[38%] w-[55%] rounded-full bg-primary-line" />
        </Block>
        <span className="flex flex-1 flex-col gap-[10%] pt-[2%]">
          <Line w="100%" strong />
          <Line w="70%" />
          <Line w="80%" />
          <span className="mt-auto block h-[18%] w-[70%] rounded-sm bg-primary" />
        </span>
      </span>
      <Line w="100%" />
      <Line w="92%" />
      <Line w="96%" />
      <Line w="60%" />
    </>
  ),
  [LanderType.PRELANDING]: (
    <>
      <Line w="26%" tone="danger" />
      <Line w="96%" strong />
      <Line w="70%" strong />
      <Block h="30%" />
      <Line w="100%" />
      <Line w="94%" />
      <Line w="90%" />
      <Line w="52%" tone="primary" />
    </>
  ),
  [LanderType.VSL]: (
    <>
      <Line w="84%" strong />
      <span className="mx-auto block w-[62%]">
        <Line w="100%" />
      </span>
      <Block h="44%" className="relative flex items-center justify-center bg-foreground/80">
        <Play className="size-[18%] fill-background text-background" strokeWidth={0} />
        <span className="absolute bottom-0 left-0 block h-[6%] w-[42%] bg-destructive" />
      </Block>
      <span className="mx-auto mt-[3%] block h-[10%] w-[55%] rounded-sm bg-primary" />
    </>
  ),
  [LanderType.WHITE_PAGE]: (
    <>
      <span className="flex h-[30%] shrink-0 gap-[6%]">
        <span className="flex flex-[1.8] flex-col gap-[14%]">
          <Line w="100%" strong />
          <Line w="60%" />
        </span>
        <Block h="60%" className="mt-[6%] flex-1" />
      </span>
      {["100%", "96%", "100%", "92%", "98%", "88%", "100%", "94%"].map((w, i) => (
        <Line key={i} w={w} />
      ))}
    </>
  ),
  [LanderType.THANK_YOU]: (
    <span className="flex h-full flex-col items-center justify-center gap-[7%]">
      <span className="block aspect-square h-[38%] rounded-full border border-ok-line bg-ok-soft" />
      <Line w="70%" strong />
      <Line w="50%" />
      <span className="block h-[8%] w-[38%] rounded-sm border border-border-strong" />
    </span>
  ),
};

/**
 * A tile's picture: the page itself, live, over its sketch. The served page
 * (`/lander/{id}/?preview=1`) is laid out at four times the tile and scaled
 * back, mounted only when the tile nears the screen and never in the way of
 * the click that opens the lander; until it loads — or when it cannot, or
 * the lander has no version yet — the sketch is what shows.
 */
export function LanderPicture({ landerId, type, hasContent, className }: { landerId: string; type: LanderType; hasContent: boolean; className?: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");

  useEffect(() => {
    if (!hasContent || near) return;
    const el = host.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        io.disconnect();
      }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasContent, near]);

  useEffect(() => {
    if (!near || state !== "loading") return;
    const timer = setTimeout(() => setState((s) => (s === "loading" ? "failed" : s)), 7000);
    return () => clearTimeout(timer);
  }, [near, state]);

  return (
    <div ref={host} aria-hidden className={cn("relative h-full w-full overflow-hidden", className)}>
      <LanderSketch type={type} />
      {near && state !== "failed" ? (
        <iframe
          src={`/lander/${landerId}/?preview=1&thumb=1`}
          title=""
          tabIndex={-1}
          loading="lazy"
          scrolling="no"
          sandbox="allow-scripts allow-same-origin"
          onLoad={() => setState("ready")}
          onError={() => setState("failed")}
          className={cn("pointer-events-none absolute left-0 top-0 origin-top-left border-0 bg-background transition-opacity duration-500", state === "ready" ? "opacity-100" : "opacity-0")}
          style={{ width: "400%", height: "400%", transform: "scale(0.25)" }}
        />
      ) : null}
    </div>
  );
}
